import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FiFileText,
  FiMapPin,
  FiMessageCircle,
} from "react-icons/fi";
import {
  getRegions,
  getCountriesByRegion,
  getStatesByCountry,
  getCitiesByState,
} from "../../../../api/regionApi";
import { getContact, updateContact } from "../../../../api/contactApi";
import { getTeleConfig, updateTeleConfig } from "../../../../api/telegramBotApi";

const CompanySettings = () => {
  const { t } = useTranslation();

  // ----------------------------------------------------------------
  // Form Data
  // ----------------------------------------------------------------
  const [formData, setFormData] = useState({
    restaurantName: "",
    companyEmail: "",
    phoneNumber: "",
    hotline: "",
    website: "",
    address: "",
    region: "",
    country: "",
    state: "",
    city: "",
    postalCode: "",
    openingHours: "",
    telegramToken: "",
    telegramChatId: "",
    telegramLanguage: "",
  });

  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // ----------------------------------------------------------------
  // Load all regions
  // ----------------------------------------------------------------
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const data = await getRegions();
        setRegions(data);
      } catch (err) {
        console.error("Failed to load regions:", err);
      }
    };
    loadRegions();
  }, []);

  // ----------------------------------------------------------------
  // Load Telegram Configuration
  // ----------------------------------------------------------------
  useEffect(() => {
    const loadTelegramConfig = async () => {
      try {
        const telegramBotConfig = await getTeleConfig();

        if (telegramBotConfig) {
          setFormData((prev) => ({
            ...prev,
            telegramToken: telegramBotConfig.botToken || "",
            telegramChatId: telegramBotConfig.chatId || "",
            telegramLanguage: telegramBotConfig.language || ""
          }));
        } else {
          console.warn("No Telegram config found.");
        }
      } catch (err) {
        console.error("Failed to load telegram config", err);
      }
    };
    loadTelegramConfig();
  }, []);

  // ----------------------------------------------------------------
  // Load Contact Info and Auto Map Locations
  // ----------------------------------------------------------------
  useEffect(() => {
    const loadContactData = async () => {
      try {
        const contact = await getContact();

        // Step 1. Fill basic info
        setFormData((prev) => ({
          ...prev,
          restaurantName: contact.restaurantName || "",
          companyEmail: contact.email || "",
          phoneNumber: contact.phoneNumber || "",
          hotline: contact.hotline || "",
          website: contact.website || "",
          address: contact.address || "",
          postalCode: contact.postalCode || "",
          openingHours: contact.openingHours || "",
          region: contact.regionId || "",
          country: contact.countryId || "",
          state: contact.stateId || "",
          city: contact.cityId || "",
        }));

        // Step 2. Load regions for matching
        const allRegions = await getRegions();
        setRegions(allRegions);

        // Step 3. Map region by name if no ID provided
        let matchedRegion = null;
        if (!contact.regionId && contact.regionName) {
          matchedRegion = allRegions.find(
            (r) =>
              r.name.toLowerCase() === contact.regionName.toLowerCase().trim()
          );
          if (matchedRegion) {
            setFormData((prev) => ({ ...prev, region: matchedRegion.id }));
          }
        } else if (contact.regionId) {
          matchedRegion = allRegions.find((r) => r.id === contact.regionId);
        }

        // Step 4. Map Country -> State -> City
        if (matchedRegion || contact.countryName) {
          const regionId = matchedRegion ? matchedRegion.id : contact.regionId;
          const countries = await getCountriesByRegion(regionId);
          setCountries(countries);

          const matchedCountry = countries.find(
            (c) =>
              c.name.toLowerCase() === contact.countryName?.toLowerCase().trim()
          );

          if (matchedCountry) {
            setFormData((prev) => ({ ...prev, country: matchedCountry.id }));

            const states = await getStatesByCountry(matchedCountry.id);
            setStates(states);

            const matchedState = states.find(
              (s) =>
                s.name.toLowerCase() === contact.stateName?.toLowerCase().trim()
            );

            if (matchedState) {
              setFormData((prev) => ({ ...prev, state: matchedState.id }));

              const cities = await getCitiesByState(matchedState.id);
              setCities(cities);

              const matchedCity = cities.find(
                (c) =>
                  c.name.toLowerCase() === contact.cityName?.toLowerCase().trim()
              );
              if (matchedCity) {
                setFormData((prev) => ({ ...prev, city: matchedCity.id }));
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to load contact or map locations:", err);
      }
    };

    loadContactData();
  }, []);

  // ----------------------------------------------------------------
  // Cascading Dropdowns
  // ----------------------------------------------------------------
  useEffect(() => {
    if (formData.region) {
      const loadCountries = async () => {
        try {
          const data = await getCountriesByRegion(formData.region);
          setCountries(data);
        } catch (err) {
          console.error("Failed to load countries:", err);
        }
      };
      loadCountries();
    }
  }, [formData.region]);

  useEffect(() => {
    if (formData.country) {
      const loadStates = async () => {
        try {
          const data = await getStatesByCountry(formData.country);
          setStates(data);
        } catch (err) {
          console.error("Failed to load states:", err);
        }
      };
      loadStates();
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.state) {
      const loadCities = async () => {
        try {
          const data = await getCitiesByState(formData.state);
          setCities(data);
        } catch (err) {
          console.error("Failed to load cities:", err);
        }
      };
      loadCities();
    }
  }, [formData.state]);

  // ----------------------------------------------------------------
  // Handle Input Change
  // ----------------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ----------------------------------------------------------------
  // Handle Save (update contact + telegram config)
  // ----------------------------------------------------------------
  const handleSave = async () => {
    try {
      const selectedRegion = regions.find((r) => r.id === Number(formData.region));
      const selectedCountry = countries.find((c) => c.id === Number(formData.country));
      const selectedState = states.find((s) => s.id === Number(formData.state));
      const selectedCity = cities.find((c) => c.id === Number(formData.city));

      const payload = {
        ...formData,
        regionId: formData.region || null,
        countryId: formData.country || null,
        stateId: formData.state || null,
        cityId: formData.city || null,
        regionName: selectedRegion?.name || null,
        countryName: selectedCountry?.name || null,
        stateName: selectedState?.name || null,
        cityName: selectedCity?.name || null,
        email: formData.companyEmail || null,
      };

      await updateContact(payload);

      await updateTeleConfig({
        botToken: formData.telegramToken,
        chatId: formData.telegramChatId,
        language: formData.telegramLanguage,
      });

      alert(t("admin.setting.update_success"));
    } catch (err) {
      console.error("Failed to save company info:", err);
    }
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="company-settings">
      <div className="settings-content-area">
        {/* Company Info Section */}
        <div className="settings-section">
          <div className="section-header">
            <FiFileText className="section-icon" />
            <h3>{t("admin.setting.company_info")}</h3>
          </div>

          <div className="form-grid">
            {[
              { name: "restaurantName", label: t("admin.setting.company_name"), type: "text" },
              { name: "companyEmail", label: t("admin.setting.company_email"), type: "email" },
              { name: "phoneNumber", label: t("admin.setting.phone_number"), type: "tel" },
              { name: "hotline", label: t("admin.setting.hotline"), type: "tel" },
              { name: "website", label: t("admin.setting.website"), type: "url" },
              { name: "openingHours", label: t("admin.setting.opening_hours"), type: "text" },
            ].map((field) => (
              <div className="form-group" key={field.name}>
                <label>{field.label}</label>
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleInputChange}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Telegram Config Section */}
        <div className="settings-section">
          <div className="section-header">
            <FiMessageCircle className="section-icon" />
            <h3>{t("admin.setting.telegram_config")}</h3>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>{t("admin.setting.telegram_token")}</label>
              <input
                type="text"
                name="telegramToken"
                value={formData.telegramToken}
                onChange={handleInputChange}
                placeholder="Bot Token"
              />
            </div>
            <div className="form-group">
              <label>{t("admin.setting.telegram_chat_id")}</label>
              <input
                type="text"
                name="telegramChatId"
                value={formData.telegramChatId}
                onChange={handleInputChange}
                placeholder="Chat ID"
              />
            </div>
            <div className="form-group">
            <label>{t("admin.setting.telegram_language")}</label>
            <select
              name="telegramLanguage"
              value={formData.telegramLanguage}
              onChange={handleInputChange}
            >
              <option value="">{t("admin.setting.select_language")}</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="settings-section">
          <div className="section-header">
            <FiMapPin className="section-icon" />
            <h3>{t("admin.setting.address_info")}</h3>
          </div>

          <div className="form-group full-width">
            <label>{t("admin.setting.address")}</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
            />
          </div>

          <div className="form-grid">
            {/* Region */}
            <div className="form-group">
              <label>{t("admin.setting.region")}</label>
              <select
                name="region"
                value={formData.region}
                onChange={handleInputChange}
              >
                <option value="">{t("admin.setting.select_region")}</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Country */}
            <div className="form-group">
              <label>{t("admin.setting.country")}</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={!formData.region}
              >
                <option value="">{t("admin.setting.select_country")}</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* State */}
            <div className="form-group">
              <label>{t("admin.setting.state")}</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                disabled={!formData.country}
              >
                <option value="">{t("admin.setting.select_state")}</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* City */}
            <div className="form-group">
              <label>{t("admin.setting.city")}</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                disabled={!formData.state}
              >
                <option value="">{t("admin.setting.select_city")}</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Postal Code */}
            <div className="form-group">
              <label>{t("admin.setting.postal_code")}</label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="form-actions">
          <button className="btn-cancel">{t("admin.setting.cancel")}</button>
          <button className="btn-save" onClick={handleSave}>
            {t("admin.setting.save_changes")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
